import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { VariableDropdownContainer } from './styles';
import { Input, Menu, Popover, Alert, Switch, Tooltip, Select, DatePicker, message } from 'antd';
import { MenuItem } from '../../../components/PageResizeDropdown/styles';
import {
  CancelButton,
  PopupActionContainer,
  PopupContainer,
  PopupLabel,
  SaveButton,
} from '../../../artboard/Page/styles';
import { useTranslation } from 'react-i18next';
import { CREATE_VARIABLE_POPUP } from './translationValues';
import { VARIABLE_TYPE } from '../../../utils/constants/appConstants';
import createFuzzySearch from '@nozbe/microfuzz'
import { scrollToTarget } from '../../../utils/groupHelper';

const NAMESPACES = ['CREATE_VARIABLE_POPUP'];

const { Option } = Select;
const Keys = {
  TAB: 9,
  ENTER: 13,
  ESCAPE: 27,
  UP: 38,
  DOWN: 40
};

const EMPTY_ARRAY = [];

const orderVariablesByGroup = (variables, types = [], allowLocal = false, relationships =[]) => {
  let filteredVariables = [...variables, ...(relationships.length > 0 ? relationships : [])];
  if (types.length > 0) {
    filteredVariables = filteredVariables.filter(v => types.includes(v.type));
  }
  const newVariables = filteredVariables
    .sort((variable1, variable2) => variable1.variableCategoryId < variable2.variableCategoryId ? -1 : 1)
    .map((variable, index) => Object.assign({}, variable, { index }));
  const groups = newVariables.reduce(
    (acc, variable) => {
      if (acc.find(a => a.name === variable.variableCategoryName) === undefined)
        return [...acc, { id: variable.variableCategoryId, name: variable.variableCategoryName }];
      return acc;
    },
    []
  );
  return { groups, variables: newVariables };
}

const VariableMentionPopup = memo((props) => {
  const { quill, target, searchTerm, onHide, onVariableSelect, position, uuid, popupPlacement, globalVariables, relationships = EMPTY_ARRAY, updateGlobalVariables,
    isMailSubject = false, getPopupContainer = () => document.body, types = EMPTY_ARRAY, allowLocal = false, onlyLocal = false, levels = EMPTY_ARRAY } = props;
  const { t } = useTranslation(NAMESPACES);
  const scrollRef = useRef();
  const popupRootRef = useRef(null);
  const createFormRootRef = useRef(null);
  const exampleInputRef = useRef();
  const examplePickerRef = useRef();
  const displayNameRef = useRef();
  const listCountInputRef = useRef();
  const variablesRef = useRef([...globalVariables, ...relationships]);
  const [ active, setActive ] = useState(0);
  const [ isCreate, toggleCreate ] = useState(false);
  const [ orderedVariables, setOrderedVariables ] = useState([]);
  const [ groups, setGroups ] = useState([]);
  const [ isList, toggleIsList ] = useState(onlyLocal);
  const [ isCountRequired, setIsCountRequired ] = useState(false);
  const [ exampleRequired, setExampleRequired ] = useState(false);
  const [ dataType, setDataType ] = useState('String');
  const [ popupPosition, setPopupPosition ] = useState(null);
  const [ isMandatory, setIsMandatory ] = useState(false);
  const [checkLength, setCheckLength] = useState(false);
  const [charLength, setLength] = useState(20);
  let relationshipCount = useRef(0);

  useEffect(() => {
    const { groups, variables } = orderVariablesByGroup([...globalVariables], types, allowLocal, relationships);
    setGroups(groups);
    setOrderedVariables(variables);
    variablesRef.current = Array.from(variables);
  }, [globalVariables, allowLocal, relationships]);

  useEffect(() => {
    if (!popupPlacement) {
      let target = document.querySelector(`[data-uuid='${uuid}']`);
      let targetElement = target.getBoundingClientRect();
      setPopupPosition(window.innerHeight / targetElement.top);
    }
  }, [uuid]);

  const upHandler = useCallback(() => {
    if(variablesRef.current.length) {
      let currentActiveIndex = active - 1;
      if (currentActiveIndex < 0) {
        currentActiveIndex = variablesRef.current.length - 1;
      }
      setActive(currentActiveIndex);
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          const el = scrollRef.current.querySelector(`[data-index='${currentActiveIndex}']`);
          scrollToTarget(scrollRef.current, el, false, false);
        }
      });
    }
  }, [active, filteredVariables]);

  const downHandler = useCallback(() => {
    if(variablesRef.current.length) {
      let currentActiveIndex = active + 1;
      if (currentActiveIndex > (variablesRef.current.length - 1)) {
        currentActiveIndex = 0;
      }
      setActive(currentActiveIndex);
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          const el = scrollRef.current.querySelector(`[data-index='${currentActiveIndex}']`);
          scrollToTarget(scrollRef.current, el, false, false);
        }
      });
    }
  }, [active, filteredVariables]);

  const escapeHandler = useCallback(() => {
    onHide();
  }, [onHide])

  const enterHandler = useCallback(() => {
    const el = scrollRef.current.querySelector(`[data-index='${active}']`);
    const variableDetails = JSON.parse(el?.dataset?.variable);
    if (variableDetails?.id) {
      const currentActive = orderedVariables.find(variable => variable.id == variableDetails.id && variable.type === variableDetails.type);
      onVariableSelect(currentActive, globalVariables);
    }
  }, [active, isCreate, orderedVariables, searchTerm, onVariableSelect, globalVariables])

  const createVariable = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const split = searchTerm.split(".");
    let listCount = null;
    let length = charLength;
    if (!(length === null || length === undefined)) {
      if (length === '') {
        length = null;
      } else {
        length = parseInt(length);
      }
    }
    if (isList) {
       listCount = listCountInputRef?.current?.state?.value;
       if (listCount === null || listCount === undefined) {
         setIsCountRequired(true);
         return;
       } else {
         listCount = parseInt(listCount);
         if (!listCount) {
           setIsCountRequired(true);
           return;
         }
       }
    }
    let exampleValue = dataType === 'Date' ? examplePickerRef.current?.picker?.input?.value : exampleInputRef.current?.state?.value || "";
    if (exampleValue === "" || exampleValue.trim() === "") {
      setExampleRequired(true);
      return;
    }
    const entity = orderedVariables.find(variable => variable.variableCategoryName.toLowerCase() === split[0].toLowerCase());
    const variableCategoryName = entity?.variableCategoryName || '';
    const displayName = displayNameRef.current?.state?.value?.trim() === '' ? null : displayNameRef.current?.state?.value?.trim();
    let newVariable = { name: split[1], placeHolderText: `${variableCategoryName}.${split[1]}`,
      variableCategoryId: entity?.variableCategoryId,
      variableCategoryName: variableCategoryName,
      length: dataType !== "Date" ? length : null,
      exampleValue: exampleValue.trim(),
      type:
        dataType === 'String'
          ? isList
            ? 'ListString'
            : 'String'
          : isList
          ? 'ListDate'
          : 'Date',
      count: listCount,
      mandatory: isMandatory,
      checkLength: checkLength,
      displayName
    };
    if(dataType === 'Date'){      
      newVariable.format = process.env.REACT_APP_VARIABLE_DATE_FORMAT;
    }
    try {
      const [savedVariable, newVariablesList] = await updateGlobalVariables(
        newVariable
      );
      if (savedVariable && newVariablesList) {
        onVariableSelect(savedVariable, newVariablesList);
      }
    } catch (e) {
      console.error(e);
      message.error(t(CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_FAILURE.key, CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_FAILURE.value))
    } finally {
      toggleCreate(false);
      onInputBlur();
      onHide();
    }
  }, [searchTerm, orderedVariables, onHide, onVariableSelect, updateGlobalVariables, dataType, isList, isMandatory, charLength])

  const createLocalVariable = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const localVariable = {
      name: searchTerm,
      placeHolderText: searchTerm,
      type: dataType,
      isList,
      variableCategoryId: 0,
      variableCategoryName: searchTerm.split('.')[0]
    }
    const [savedVariable, newVariablesList] = await updateGlobalVariables(
      localVariable,
      true
    );
    onVariableSelect(localVariable, newVariablesList);
  }, [searchTerm, onVariableSelect, dataType, isList]);

  const preventDefault = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onInputBlur = useCallback(() => {
    if (quill?.root) {
      quill.root.focus({
        preventScroll: true
      });
    }
  }, [quill]);

  // const isVariableMatched = !!globalVariables.find(variable => variable.placeHolderText.toLowerCase().startsWith(searchTerm?.toLowerCase()));

  useEffect(() => {
    if (target === undefined) return;
    const targetEventListener = (event) => {
      if (isCreate) {
        if (event.keyCode === Keys.ESCAPE) {
          event.preventDefault();
          event.stopPropagation();
          toggleCreate(false);
        }
        return;
      }
      if (event.keyCode === Keys.UP) { upHandler(); }
      else if (event.keyCode === Keys.DOWN) { downHandler(); }
      else if (event.keyCode === Keys.ENTER) { enterHandler(); }
      else if (event.keyCode === Keys.ESCAPE) { escapeHandler(); }
    };
    target.addEventListener("keydown", targetEventListener);
    return () => {
      target.removeEventListener("keydown", targetEventListener);
    };
  }, [target, isCreate, upHandler, downHandler, enterHandler, escapeHandler]);

  useEffect(() => {
    if (quill === undefined) return;
    quill.keyboard.addBinding(
      {
        key: Keys.UP
      },
      upHandler
    );

    quill.keyboard.addBinding(
      {
        key: Keys.DOWN
      },
      downHandler
    );

    quill.keyboard.addBinding(
      {
        key: Keys.ENTER
      },
      enterHandler
    );

    quill.keyboard.bindings[Keys.ENTER].unshift(
      quill.keyboard.bindings[Keys.ENTER].pop()
    );

    quill.keyboard.addBinding(
      {
        key: Keys.ESCAPE
      },
      escapeHandler
    );
    return () => {
      quill.keyboard.bindings[Keys.UP].pop();
      quill.keyboard.bindings[Keys.DOWN].pop();
      quill.keyboard.bindings[Keys.ENTER].shift();
      quill.keyboard.bindings[Keys.ESCAPE].pop();
    }
  }, [quill, upHandler, downHandler, enterHandler, escapeHandler ]);

  const isValidRelation = (splitTerm) => {
    if (splitTerm.length <= 2) return true;

    for (let i = 1; i < splitTerm.length - 1; i++) {
      const curr = splitTerm[i].toLowerCase();
      const prev = splitTerm[i - 1].toLowerCase();
      const entity = relationships.find(relation => relation.name?.toLowerCase() === curr&& relation.placeHolderText?.toLowerCase().startsWith(prev));
      if (!entity) {
        return false;
      }
    }
    return true;
  };

  let filteredVariables = Array.from(orderedVariables);
  if(!searchTerm && levels.length > 0) {
    filteredVariables = orderedVariables.filter(variable => 
      variable.variableCategoryId === 'relation'
      ? levels.some(level => 
        variable.placeHolderText.startsWith(levels[0])
        && (!variable.isList || levels[1] === variable.placeHolderText.split(".")[1]))
      : true
    );
  }
  if (searchTerm) {
    const splitTerm = searchTerm.split('.');
    let currentEntity = undefined;
    if(levels.length === 0 || splitTerm.length <= levels.length + 2) {
      if(splitTerm.length > 1) {
          if(!isValidRelation(splitTerm)) {
            filteredVariables = [];
          }
        currentEntity = orderedVariables.find(entity => entity.name.toLowerCase() === splitTerm[splitTerm.length-2].toLowerCase());
        if(currentEntity && filteredVariables.length > 0) {
          filteredVariables = orderedVariables.filter(variable => 
            variable.variableCategoryId === 'relation'
            ? variable.placeHolderText.toLowerCase().startsWith(currentEntity?.name?.toLowerCase())
            : variable.variableCategoryName.toLowerCase() === currentEntity.name.toLowerCase()
          );
        }

        if(levels.length) {
            if(splitTerm.length <= levels.length) {
            filteredVariables = filteredVariables.filter(variable => 
              variable.variableCategoryId === 'relation'
              ? variable.isList ? levels[splitTerm.length - 1] === variable.name : true 
              : true
            )
          } else if(splitTerm.length > levels.length + 1) {
            filteredVariables = filteredVariables.filter(variable => variable.variableCategoryId !== 'relation')
          }
      }
      } else if(levels.length > 0) {
        filteredVariables = filteredVariables.filter(variable => 
            variable.variableCategoryId === 'relation'
            ? variable.isList ? levels[splitTerm.length] === variable.name : true 
            : true
          )
      }
    }
    const fuzzySearch = createFuzzySearch( filteredVariables, { key: 'placeHolderText', strategy: 'default' });
    const results = fuzzySearch(currentEntity ? currentEntity.name + '.' + searchTerm.substring(searchTerm.lastIndexOf('.') + 1): searchTerm);
    filteredVariables = results.map(result => result.item);
    variablesRef.current = filteredVariables;
  }
  const searchTermCategory = searchTerm ? searchTerm.split(".")[0] : undefined;
  let showCreate = filteredVariables.length === 0;
  let localVariable = false;
  if (searchTermCategory) {
    let categoryDetails = groups.find(group => group.name.toLowerCase() === searchTermCategory.toLowerCase());
    if (categoryDetails === undefined || categoryDetails?.id === 0) {
      localVariable = allowLocal;
      showCreate = allowLocal;
    } else if (onlyLocal) {
      localVariable = onlyLocal;
      showCreate = onlyLocal;
    } else {
      localVariable = false;
      showCreate = searchTerm.split('.').length === 2;
    }
  }

  let groupLength = 0;

  return ( 
     <div
      ref={popupRootRef}
      data-variable-mention-popup="true"
      style={{
      position: position ? 'absolute' : "relative",
      left: position ? `${position.left}px` : undefined,
      top: position ? `${position.bottom + 10}px` : undefined,
      zIndex: 10000
     }}>
      {globalVariables.length === 0 && (
        <Alert
          message={t(CREATE_VARIABLE_POPUP.EMPTY_VARIABLES_LIST_MESSAGE.key, CREATE_VARIABLE_POPUP.EMPTY_VARIABLES_LIST_MESSAGE.value)}
          type="error"
        />
      )}
      {globalVariables.length!==0 && (
      <VariableDropdownContainer ref={scrollRef} showVariableList={filteredVariables.length !== 0 || showCreate}>
        {!isCreate && (<Menu>
          { groups.map((groupDetail) => {
              const groupVariables = filteredVariables.filter(variable =>
                isMailSubject
                  ? variable.variableCategoryName === groupDetail.name && variable.type !== 'ListString' && variable.type !== 'ListDate'
                  : variable.variableCategoryName === groupDetail.name
              );
            if (groupVariables.length > 0) {
              const currentLength = groupLength;
              groupLength += groupVariables.length;
              return (
                <Menu.ItemGroup title={groupDetail.name}>
                  {groupVariables.length !== 0 && groupVariables.map((variable, index) => {
                    return (
                      <Menu.Item key={variable.placeHolderText} id={`menu-item-${variable.placeHolderText}`}>
                        <MenuItem variable={1} data-index={currentLength + index} active={currentLength + index === active} data-variable={JSON.stringify(variable)} onMouseDown={(domEvent) => {
                          domEvent.preventDefault();
                          domEvent.stopPropagation();
                          onVariableSelect(variable, globalVariables);
                        }}>
                          <div>{variable.placeHolderText}</div>
                          {(variable.type === 'ListString' || variable.type === 'ListDate') && (
                            <Tooltip placement="right" title={t(CREATE_VARIABLE_POPUP.VARIABLES_LIST_MULTI_VALUE_VARIABLE_TOOLTIP.key, CREATE_VARIABLE_POPUP.VARIABLES_LIST_MULTI_VALUE_VARIABLE_TOOLTIP.value)}>
                              <div className="variable-type">L</div>
                            </Tooltip>
                          )}
                          {variable.type === 'Date' && (
                            <Tooltip placement="right" title={t(CREATE_VARIABLE_POPUP.VARIABLES_LIST_DATE_VARIABLE_TOOLTIP.key, CREATE_VARIABLE_POPUP.VARIABLES_LIST_DATE_VARIABLE_TOOLTIP.value)}>
                              <div className="variable-type">D</div>
                            </Tooltip>
                          )}
                          {variable.type === 'html' && (
                            <Tooltip placement="right" title={t(CREATE_VARIABLE_POPUP.VARIABLES_LIST_HTML_VARIABLE_TOOLTIP.key, CREATE_VARIABLE_POPUP.VARIABLES_LIST_HTML_VARIABLE_TOOLTIP.value)}>
                              <div className="variable-type">H</div>
                            </Tooltip>
                          )}
                          {variable.type === 'perfectdoc-relationship' && variable.isList === true && (
                            <Tooltip placement="right" title={t(CREATE_VARIABLE_POPUP.ONE_TO_MANY_RELATION_TOOLTIP.key, CREATE_VARIABLE_POPUP.ONE_TO_MANY_RELATION_TOOLTIP.value)}>
                              <div className="variable-type">N</div>
                            </Tooltip>
                          )}
                        </MenuItem>
                      </Menu.Item>
                    );
                  })}
                </Menu.ItemGroup>
              );
            }
            return null;
          })}
          {filteredVariables.length === 0 && showCreate && (
            <Menu.Item key={'create-variable'}>
              <MenuItem active={active === 0} onMouseDown={domEvent => {
                domEvent.preventDefault();
                domEvent.stopPropagation();
                toggleCreate(true);
              }}>
              {t(CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_LABEL.key, CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_LABEL.value)} &nbsp; <strong> {searchTerm} </strong>
              </MenuItem>
            </Menu.Item>
          )}
        </Menu>)}
        {isCreate && (
          <Popover
            placement={
              popupPlacement ? popupPlacement : popupPosition < 1.5
                ? 'top'
                : popupPosition > 1.5 && popupPosition < 5
                ? 'left'
                : 'bottom'
            }
            getPopupContainer={(triggerNode) => {
              try {
                const customRoot = getPopupContainer(triggerNode);
                if (customRoot && customRoot !== document.body) return customRoot;
              } catch (e) {
                /* noop */
              }
              return popupRootRef.current || (triggerNode && triggerNode.parentElement) || document.body;
            }}
            content={
              <PopupContainer ref={createFormRootRef} data-variable-mention-popup="true" onClick={preventDefault}>
                <PopupLabel>{t(CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_NAME_LABEL.key, CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_NAME_LABEL.value)}</PopupLabel>
                <Input
                  name="variableName"
                  placeholder={t(CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_NAME_PLACEHOLDER.key, CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_NAME_PLACEHOLDER.value)}
                  autoComplete="off"
                  disabled={true}
                  value={searchTerm}
                />
                {!isMailSubject && !localVariable && <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                  <Switch style={{ marginRight: 12 }} checked={isList} onChange={(value) => {
                    toggleIsList(value);
                    setDataType('String');
                    if (!value) {
                      setIsCountRequired(false);
                    }
                  }}
                  disabled={dataType === "Date" || onlyLocal}
                  />
                  <span> {t(CREATE_VARIABLE_POPUP.LIST_OF_VALUES_LABEL.key, CREATE_VARIABLE_POPUP.LIST_OF_VALUES_LABEL.value)} </span>
                </div>}
                {!isList && !onlyLocal &&
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                  <PopupLabel> {t(CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_DATA_TYPE_LABEL.key, CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_DATA_TYPE_LABEL.value)} </PopupLabel>
                      <Select
                        labelInValue
                        defaultValue={{ key: 'String' }}
                        style={{ width: 80, marginLeft: '30px', fontSize:'13px' }}
                        getPopupContainer={(trigger) =>
                          createFormRootRef.current || trigger.parentElement || document.body
                        }
                        onChange={e => {
                          setDataType(e.key);
                          if (e.key === VARIABLE_TYPE.DATE) {
                            setCheckLength(false);
                            setLength(null);
                          }
                        }}
                      >
                        <Option value="String"> {t(CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_DATA_TYPE_STRING_OPTION.key, CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_DATA_TYPE_STRING_OPTION.value)} </Option>
                        <Option value="Date"> {t(CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_DATA_TYPE_DATE_OPTION.key, CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_DATA_TYPE_DATE_OPTION.value)} </Option>
                      </Select>
                  </div>
                }
                {
                  <>
                    <PopupLabel>{t(CREATE_VARIABLE_POPUP.DISPLAY_NAME_LABEL.key, CREATE_VARIABLE_POPUP.DISPLAY_NAME_LABEL.value)}</PopupLabel>
                    <Input
                      placeholder={t(CREATE_VARIABLE_POPUP.DISPLAY_NAME.key, CREATE_VARIABLE_POPUP.DISPLAY_NAME.value)} 
                      autoComplete="off"
                      name="displayName"
                      ref={displayNameRef}
                      maxlength="50"
                      onKeyDown={(e) => {
                        if (e.key === ' ' && e.target.value.trim() === '') {
                          e.preventDefault();
                        }
                      }}
                    />
                  </>
                }
                {!localVariable && isList && (
                  <>
                    <Input
                      placeholder={t(CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_MAXIMUM_LINES_PLACEHOLDER.key, CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_MAXIMUM_LINES_PLACEHOLDER.value)} 
                      autoComplete="off"
                      name="maxLinesInList"
                      ref={listCountInputRef}
                      onFocus={() => setIsCountRequired(false)}
                    />
                    {isCountRequired && (
                      <div style={{ color: 'red', fontSize: 'smaller', marginBottom: 10 }}>{t(CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_REQUIRED_INPUT_LABEL.key, CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_REQUIRED_INPUT_LABEL.value)}</div>
                    )}
                  </>
                )}
                {!localVariable &&
                <>
                <PopupLabel>{t(CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_EXAMPLE_LABEL.key, CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_EXAMPLE_LABEL.value)}</PopupLabel>
                {dataType !== 'Date' ? <Input
                  placeholder={t(CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_EXAMPLE_PLACEHOLDER.key, CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_EXAMPLE_PLACEHOLDER.value)}
                  autoComplete="off"
                  name="variableDefault"
                  ref={exampleInputRef}
                  onFocus={() => setExampleRequired(false)}
                  onKeyDown={(e) => {
                    if (e.key === ' ' && e.target.value.trim() === '') {
                      e.preventDefault();
                    }
                  }}
                /> : <DatePicker 
                  placeholder={t(CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_EXAMPLE_PLACEHOLDER.key, CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_EXAMPLE_PLACEHOLDER.value)}
                  name="variableDefaultPicker"
                  onFocus={() => setExampleRequired(false)}
                  ref={examplePickerRef}
                  style={{width: '100%'}}
                  popupStyle={{zIndex: 9999999}}
                  getCalendarContainer={(trigger) =>
                    createFormRootRef.current || trigger.parentElement || document.body
                  }
                  format='DD-MM-YYYY' />}
                {exampleRequired && (
                  <div style={{ color: 'red', fontSize: 'smaller', marginBottom: 10 }}>{t(CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_REQUIRED_INPUT_LABEL.key, CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_REQUIRED_INPUT_LABEL.value)}</div>
                )}
                <h5 style={{fontSize: '16px', fontWeight: 'bold', marginBottom: '6px'}}>{t(CREATE_VARIABLE_POPUP.VALIDATIONS_LABEL.key, CREATE_VARIABLE_POPUP.VALIDATIONS_LABEL.value)}</h5>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                  <Switch style={{ marginRight: 12 }} checked={isMandatory} onChange={(checked) => setIsMandatory(checked)} />
                  <span>{t(CREATE_VARIABLE_POPUP.CREATE_NEW_REQUIRED_LABEL.key, CREATE_VARIABLE_POPUP.CREATE_NEW_REQUIRED_LABEL.value)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                  <Switch style={{ marginRight: 12 }} checked={checkLength} disabled={dataType === VARIABLE_TYPE.DATE} onChange={(checked) => {
                    setCheckLength(checked);
                    if (checked) {
                      setLength(20);
                    }
                  }} />
                  <span>{t(CREATE_VARIABLE_POPUP.VALIDATE_MAX_CHARACTERS.key, CREATE_VARIABLE_POPUP.VALIDATE_MAX_CHARACTERS.value)}</span>
                </div>
                <PopupLabel>{t(CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_LENGTH_LABEL.key, CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_LENGTH_LABEL.value)}</PopupLabel>
                <Input
                  placeholder={t(CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_LENGTH_PLACEHOLDER.key, CREATE_VARIABLE_POPUP.CREATE_NEW_VARIABLE_LENGTH_PLACEHOLDER.value)}
                  autoComplete="off"
                  disabled={!checkLength}
                  type="number"
                  name="variableLength"
                  value={charLength}
                  min={1}
                  onChange={e => setLength(e.target.value)}
                />
                </>}
                <PopupActionContainer>
                  <CancelButton size="default" onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleCreate(false);
                    // onInputBlur();
                  }}>
                    {t(CREATE_VARIABLE_POPUP.CANCEL_CREATE_NEW_VARIABLE_BUTTON.key, CREATE_VARIABLE_POPUP.CANCEL_CREATE_NEW_VARIABLE_BUTTON.value)}
                  </CancelButton>
                  <SaveButton
                    type="primary"
                    size="default"
                    onMouseDown={localVariable ? createLocalVariable : createVariable}
                    // disabled={}
                  >
                    {t(CREATE_VARIABLE_POPUP.SAVE_NEW_VARIABLE_BUTTON.key, CREATE_VARIABLE_POPUP.SAVE_NEW_VARIABLE_BUTTON.value)}
                  </SaveButton>
                </PopupActionContainer>
              </PopupContainer>
            }
            title=""
            trigger="click"
            visible={true}
          >
            <div style={{ fontSize: 1 }}> Create </div>
          </Popover>
        )}
      </VariableDropdownContainer>
      )}
    </div>
  );
})
export default VariableMentionPopup;